/**
 * @file TradingBody.jsx
 * @description Provides main trading interface and functionality
 *
 * @section Responsibilities
 * - Display trading interface
 * - Handle trading operations
 * - Manage trading state
 * - Process trading data
 * - Handle trading errors
 * - Provide trading controls
 *
 * @section Dependencies
 * - TradingService: Trading operations
 * - DataService: Data processing
 * - StateService: State management
 * - ControlService: Control handling
 *
 * @section Integration Points
 * - Trading API: Trading operations
 * - Data System: Data processing
 * - State System: State management
 * - Control System: Trading controls
 *
 * @section State Management
 * - tradingState: Trading status
 * - operationState: Operation status
 * - dataState: Data status
 * - errorState: Error handling
 *
 * @section Events
 * - onTrade: Trading operations
 * - onDataProcess: Data processing
 * - onStateChange: State updates
 * - onError: Error handling
 *
 * @section Props
 * @prop {Object} tradingConfig - Trading configuration
 * @prop {Function} onComplete - Completion callback
 * @prop {Object} initialData - Initial trading data
 * @prop {Boolean} autoTrade - Controls auto-trading
 */

import React, { useState, useEffect } from "react";
import {
  OrderSides,
  OrderTypes,
  OrderDurations,
  OrderOptionTypes,
  OrderClasses,
  MultilegOrderTypes,
  MultilegOrderSides,
} from "./tradingVars";

const DEFAULT_LEG = { strike: "", optionType: "", side: "", quantity: "" };

function TradingBody({
  orderClass,
  data,
  setOrderBody,
  getExpiration,
  prefilledOrder,
  updatedStrikes,
  strikeOptionObjects,
}) {
  const ORDER_TYPES = {
    MARKET: "market",
    LIMIT: "limit",
    STOP: "stop",
    STOP_LIMIT: "stop_limit",
    DEBIT: "debit",
    CREDIT: "credit",
    EVEN: "even",
  };

  // const [order, setOrder, clearOrder] = useForm({...orderData});

  const [side, setSide] = useState("");
  const [quantity, setQuantity] = useState("");
  const [type, setType] = useState("");
  const [duration, setDuration] = useState("");
  const [tag, setTag] = useState("tag");

  const [expiration, setExpiration] = useState();
  const [strike, setStrike] = useState();

  const [stop, setStop] = useState();
  const [price, setPrice] = useState();

  const [optionType, setOptionType] = useState();
  const [optionSymbol, setOptionSymbol] = useState();

  const [legs, setLegs] = useState([{ ...DEFAULT_LEG }, { ...DEFAULT_LEG }]);

  let orderData = {
    class: orderClass,
    symbol: data.symbol,
    side: side,
    quantity: quantity,
    type: type,
    duration: duration,
    tag: tag,
    stop: stop,
    price: price,
    option_symbol: optionSymbol,
  };

  useEffect(() => {
    if (orderClass === "multileg") {
      buildMultilegBody(legs);
      return;
    }
  }, [type, duration, orderClass, price, legs, strikeOptionObjects]);

  useEffect(() => {
    if (orderClass !== OrderClasses.OPTION.value) return;

    const optionObj = (strikeOptionObjects || []).find(
      (o) => String(o.strike) === String(strike) && o.option_type === optionType
    );
    setOptionSymbol(optionObj?.symbol || "");
  }, [strike, optionType, strikeOptionObjects, orderClass]);

  useEffect(() => {
    if (orderClass === "multileg") return;

    if (type === ORDER_TYPES.MARKET || type === ORDER_TYPES.EVEN) {
      delete orderData.stop;
      delete orderData.price;
    } else if (
      type === ORDER_TYPES.LIMIT ||
      type === ORDER_TYPES.DEBIT ||
      type === ORDER_TYPES.CREDIT
    ) {
      delete orderData.stop;
    } else if (type === ORDER_TYPES.STOP) {
      delete orderData.price;
    }

    if (orderClass != OrderClasses.OPTION.value) {
      delete orderData.option_symbol;
    }

    setOrderBody(orderData);
  }, [side, quantity, type, duration, tag, stop, price, optionType, optionSymbol]);

  useEffect(() => {
    setSide("");
    setQuantity("");
    setType("");
    setDuration("");
    setStop("");
    setPrice("");
    setExpiration("");
    setStrike("");
    setOptionType("");
    setOptionSymbol("");
    setLegs([{ ...DEFAULT_LEG }, { ...DEFAULT_LEG }]);
  }, [data]);

  useEffect(() => {
    if (prefilledOrder) {
      setSide(prefilledOrder.side);
      setQuantity(prefilledOrder.quantity);
      setType(OrderTypes.MARKET.value);
      setDuration(OrderDurations.DAY.value);
      orderClass = "equity";
    }
  }, [prefilledOrder]);

  useEffect(() => {
    getExpiration(expiration);
  }, [expiration]);

  const buildMultilegBody = (currentLegs) => {
    const leg = currentLegs.map((l) => {
      const optionObj = (strikeOptionObjects || []).find(
        (o) =>
          String(o.strike) === String(l.strike) &&
          o.option_type === l.optionType
      );
      return {
        option_symbol: optionObj?.symbol || "",
        side: l.side,
        quantity: l.quantity,
      };
    });
    const body = {
      class: "multileg",
      symbol: data.symbol,
      type,
      duration,
      leg,
    };

    if (type === MultilegOrderTypes.DEBIT.value || type === MultilegOrderTypes.CREDIT.value) {
      body.price = price;
    }

    setOrderBody(body);
  };

  const updateLeg = (index, field, value) => {
    const newLegs = legs.map((leg, i) =>
      i === index ? { ...leg, [field]: value } : leg
    );
    setLegs(newLegs);
    buildMultilegBody(newLegs);
  };

  const addLeg = () => {
    if (legs.length < 4) {
      const newLegs = [...legs, { ...DEFAULT_LEG }];
      setLegs(newLegs);
      buildMultilegBody(newLegs);
    }
  };

  const removeLeg = (index) => {
    if (legs.length > 2) {
      const newLegs = legs.filter((_, i) => i !== index);
      setLegs(newLegs);
      buildMultilegBody(newLegs);
    }
  };


  const OptionTrade = () => (
    <div className="row">
      <div className="form-field">
        <select
          name=""
          id=""
          onChange={(e) => setExpiration(e.target.value)}
          value={expiration}
        >
          <option value="">Expirations</option>
          {data?.expirations?.date &&
            Object.keys(data?.expirations?.date).map((expiration, index) => (
              <option key={data.expirations.date[index]}>
                {data.expirations.date[index]}
              </option>
            ))}
        </select>
      </div>
      <div className="form-field">
        <select
          name=""
          id=""
          disabled={!updatedStrikes}
          onChange={(e) => setStrike(e.target.value)}
          value={strike}
        >
          <option value="">Strike</option>
          {updatedStrikes &&
            updatedStrikes.map((strike, index) => (
              <option key={index}>{strike}</option>
            ))}
        </select>
      </div>
      <div className="form-field">
        <select
          name=""
          id=""
          disabled={!strike}
          onChange={(e) => setOptionType(e.target.value)}
          value={optionType}
        >
          <option value="">Type</option>
          {Object.keys(OrderOptionTypes).map((opType, index) => (
            <option
              key={OrderOptionTypes[opType].value}
              value={OrderOptionTypes[opType].value}
            >
              {OrderOptionTypes[opType].name}
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  const MultilegTrade = () => (
    <div className="row-leg">
      <div className="row">
        <div className="form-field">
          <select
            onChange={(e) => setExpiration(e.target.value)}
            value={expiration}
          >
            <option value="">Expiration</option>
            {data?.expirations?.date &&
              Object.keys(data?.expirations?.date).map((_, index) => (
                <option key={data.expirations.date[index]}>
                  {data.expirations.date[index]}
                </option>
              ))}
          </select>
        </div>
      </div>

      {legs.map((leg, index) => (
        <div key={index} className="row" style={{ alignItems: "center", marginBottom: 6 }}>
          <span style={{ color: "#aaa", minWidth: 60, fontSize: 12 }}>Leg {index + 1}</span>
          <div className="form-field">
            <select
              disabled={!updatedStrikes}
              value={leg.strike}
              onChange={(e) => updateLeg(index, "strike", e.target.value)}
            >
              <option value="">Strike</option>
              {updatedStrikes &&
                updatedStrikes.map((s, i) => (
                  <option key={i} value={s}>{s}</option>
                ))}
            </select>
          </div>
          <div className="form-field">
            <select
              disabled={!leg.strike}
              value={leg.optionType}
              onChange={(e) => updateLeg(index, "optionType", e.target.value)}
            >
              <option value="">Type</option>
              <option value="call">Call</option>
              <option value="put">Put</option>
            </select>
          </div>
          <div className="form-field">
            <select
              value={leg.side}
              onChange={(e) => updateLeg(index, "side", e.target.value)}
            >
              <option value="">Side</option>
              {Object.keys(MultilegOrderSides).map((k) => (
                <option key={k} value={MultilegOrderSides[k].value}>
                  {MultilegOrderSides[k].name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-field">
            <input
              type="number"
              placeholder="Qty"
              value={leg.quantity}
              onChange={(e) => updateLeg(index, "quantity", e.target.value)}
              style={{ width: 70 }}
            />
          </div>
          {legs.length > 2 && (
            <button
              type="button"
              onClick={() => removeLeg(index)}
              style={{ background: "none", border: "none", color: "#e55", cursor: "pointer", fontSize: 16 }}
            >✕</button>
          )}
        </div>
      ))}

      {legs.length < 4 && (
        <div className="row">
          <button
            type="button"
            onClick={addLeg}
            style={{ background: "none", border: "1px solid #aaa", color: "#aaa", borderRadius: 4, padding: "2px 12px", cursor: "pointer", fontSize: 12, marginTop: 4 }}
          >+ Add leg</button>
        </div>
      )}
    </div>
  );

  return (
    <>
      {orderClass === "option" ? (
        <OptionTrade />
      ) : orderClass === "multileg" ? (
        <MultilegTrade />
      ) : null}

      {orderClass !== "multileg" && (
        <div className="row">
          <div className="form-field">
            <select
              onChange={(e) => setSide(e.target.value)}
              value={side}
            >
              <option value="">Side</option>
              {Object.keys(data).length !== 0 &&
                Object.keys(
                  orderClass === OrderClasses.OPTION.value
                    ? MultilegOrderSides
                    : OrderSides
                ).map((oSide) => {
                  const sides =
                    orderClass === OrderClasses.OPTION.value
                      ? MultilegOrderSides
                      : OrderSides;
                  return (
                    <option key={sides[oSide].value} value={sides[oSide].value}>
                      {sides[oSide].name}
                    </option>
                  );
                })}
            </select>
          </div>
          <div className="form-field">
            <input
              type="number"
              placeholder="Quantity"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
            />
          </div>
        </div>
      )}

      <div className="row">
        <div className="form-field">
          <select
            onChange={(e) => setType(e.target.value)}
            value={type}
          >
            <option value="">Type</option>
            {orderClass === "multileg"
              ? Object.keys(MultilegOrderTypes).map((k) => (
                  <option key={k} value={MultilegOrderTypes[k].value}>
                    {MultilegOrderTypes[k].name}
                  </option>
                ))
              : Object.keys(data).length !== 0 &&
                Object.keys(OrderTypes).map((oType) => (
                  <option
                    key={OrderTypes[oType].value}
                    value={OrderTypes[oType].value}
                  >
                    {OrderTypes[oType].name}
                  </option>
                ))}
          </select>
        </div>
        {orderClass === "multileg" &&
          (type === MultilegOrderTypes.DEBIT.value ||
            type === MultilegOrderTypes.CREDIT.value) && (
            <div className="form-field">
              <input
                type="number"
                placeholder="Price"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
              />
            </div>
          )}
        {(orderClass === "equity" || orderClass === "option") && (
          <>
            {type === "limit" && (
              <div className="form-field">
                <input
                  type="number"
                  placeholder="Price"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                />
              </div>
            )}
            {type === "stop" && (
              <div className="form-field">
                <input
                  type="number"
                  placeholder="Stop"
                  value={stop}
                  onChange={(e) => setStop(e.target.value)}
                />
              </div>
            )}
            {type === "stop_limit" && (
              <>
                <div className="form-field">
                  <input
                    type="number"
                    placeholder="Stop"
                    value={stop}
                    onChange={(e) => setStop(e.target.value)}
                  />
                </div>
                <div className="form-field">
                  <input
                    type="number"
                    placeholder="Limit"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                  />
                </div>
              </>
            )}
          </>
        )}
        <div className="form-field">
          <select
            onChange={(e) => setDuration(e.target.value)}
            value={duration}
          >
            <option value="">Duration</option>
            {Object.keys(data).length !== 0 &&
              Object.keys(OrderDurations).map((oDuration) => (
                <option
                  key={OrderDurations[oDuration].value}
                  value={OrderDurations[oDuration].value}
                >
                  {OrderDurations[oDuration].name}
                </option>
              ))}
          </select>
        </div>
      </div>
    </>
  );
}

export default TradingBody;
